import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Check, Info, X } from 'lucide-react';
import { t } from '../lib/i18n';

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: number;
  kind: ToastKind;
  text: string;
  /** İsteğe bağlı tek eylem — "Yeniden Bağlan" gibi. */
  action?: { label: string; onClick: () => void };
}

interface ToastProps {
  toast: ToastMessage | null;
  onDismiss: () => void;
  isDarkMode: boolean;
}

const AUTO_DISMISS_MS = 3_000;
/** Düğmeli bildirim biraz daha kalır: "Geri al"a uzanacak vakit olsun. */
const ACTION_DISMISS_MS = 5_000;

const TONE: Record<ToastKind, { icon: typeof Check; accent: string }> = {
  success: { icon: Check, accent: 'text-emerald-400' },
  error: { icon: AlertTriangle, accent: 'text-rose-400' },
  info: { icon: Info, accent: 'text-[#46CBE2]' },
};

/**
 * Tek yuvalı bildirim. Yığın yok: aynı anda birden fazla şey söylemek
 * gerekmiyor ve üst üste binen kutular kayıt ekranının altını kapatıyordu.
 *
 * Hepsi kendiliğinden kapanır; eylem taşıyanlar biraz daha geç.
 */
export const Toast: React.FC<ToastProps> = ({ toast, onDismiss, isDarkMode }) => {
  const hasAction = Boolean(toast?.action);

  // App her çizimde yeni bir onDismiss veriyor (kayıt sürerken saniyede bir).
  // Zamanlayıcı ona bağlı olsaydı her seferinde baştan başlar, balon hiç kapanmazdı.
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => dismissRef.current(), hasAction ? ACTION_DISMISS_MS : AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [toast, hasAction]);

  if (!toast) return null;

  const { icon: Icon, accent } = TONE[toast.kind];

  return (
    <div
      role="status"
      aria-live="polite"
      // Telefonda üstte: altta sekme çubuğu ve kayıt düğmesi var.
      className="fixed top-3 left-3 right-3 md:top-auto md:left-auto md:bottom-5 md:right-5 z-[60] md:max-w-sm animate-transcript-in"
    >
      <div
        className={`flex items-start gap-3 pl-3.5 pr-2.5 py-3 rounded-xl border shadow-2xl backdrop-blur-xl ${
          isDarkMode
            ? 'bg-[#19191C]/95 border-[#45454C] text-neutral-200 shadow-black/50'
            : 'bg-white/95 border-neutral-200 text-neutral-800 shadow-neutral-300/60'
        }`}
      >
        <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${accent}`} />
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="text-[13px] leading-relaxed">{toast.text}</p>
          {toast.action && (
            <button
              onClick={() => {
                toast.action?.onClick();
                onDismiss();
              }}
              className="text-[12.5px] font-semibold text-[#46CBE2] hover:text-[#7BDAEA] transition-colors"
            >
              {toast.action.label}
            </button>
          )}
        </div>
        <button
          onClick={onDismiss}
          aria-label={t('Bildirimi kapat', 'Dismiss notification')}
          className="p-1 rounded text-neutral-500 hover:text-[#F0EEE6] transition-colors shrink-0"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
